import { BetSlipType } from '@bpos';
import { BetType } from '@bpos/common';
import { RewardTargetType } from '@bpos/v1/sports-promo/tokens';
import { BetBuilderProvider } from '@cds/betting-offer/add-ons';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { NOT_APPLICABLE, trackingConstants } from '@frontend/sports/tracking/feature';
import { MarqueeType } from '@frontend/sports/types/components/content';
import { TrackingData } from '@frontend/vanilla/core';
import { Decimal } from 'decimal.js';
import { toLower, upperFirst } from 'lodash-es';
import { BetBuilderTrackingValues } from 'packages/sports/web/app/src/bet-builder/bet-builder-tracking';
import { SourceOfPick } from 'packages/sports/web/app/src/betslip-base/models/pick-models';
import { RewardTokenType } from 'packages/sports/web/app/src/tokens-base/token-base.models';

import { BetslipType } from '../../../core/betslip-type';
import { BetslipGroupInformation } from '../../../core/groups/betslip-group-information';
import { BetslipBetBuilderPick } from '../../../core/picks/betslip-bet-builder-pick';
import { BetslipPick } from '../../../core/picks/betslip-pick';
import { BetslipV1Pick } from '../../../core/picks/betslip-v1-pick';
import { BetslipV2OptionMarketPick } from '../../../core/picks/betslip-v2-option-market-pick';
import { BetslipV2ParticipantPick } from '../../../core/picks/betslip-v2-participant-pick';
import { BetslipV2Pick } from '../../../core/picks/betslip-v2-pick';
import { IBetslipV2PickWinParticipantMarket, IPickTracking, ParticipantPickType, PickType, PriceType } from '../../../core/picks/pick-models';
import {
    BetslipV2HorseRaceOptionMarketPick,
    BetslipV2HorseRaceWinParticipantPick,
    BetslipV2HorseRaceXCastPick,
} from '../../../core/picks/sport-specific/betslip-v2-horse-race-picks';
import { ISucceededSlipResult } from '../../betplacement/models';
import { mapBetslipType } from '../../result/services/result.utils';
import { AccaBoostToken, IRewardToken } from '../../reward-tokens/reward-tokens.model';
import { getSelectedTokenForContext } from '../../reward-tokens/services/linear-reward-tokens.utils';
import { getAccaBoostRatio } from '../../reward-tokens/services/reward-tokens.utils';
import { TokensRecord } from '../../reward-tokens/state';
import { IBetslipTypeState } from '../../types/state';
import { BetSourceTracking, PickTypeTracking, PriceTypeTracking, RewardTokenTracking, TransactionAffiliate } from '../tracking.models';

export class BetslipTrackingHelper {
    static getTransactionAffiliation(picks: BetslipPick[]): string {
        let hasLivePicks = false;
        let hasNonLivePicks = false;

        picks.forEach((pick) => {
            if (pick.isLive) {
                hasLivePicks = true;
            }
            if (!pick.isLive) {
                hasNonLivePicks = true;
            }
        });

        if (hasLivePicks && hasNonLivePicks) {
            return TransactionAffiliate.MainAndLive;
        }
        if (hasLivePicks) {
            return TransactionAffiliate.Livebook;
        }

        return TransactionAffiliate.Mainbook;
    }

    static getSourceOfBet(betSource: BetSourceTracking, stakePerPick: number | null, typeId: string, rewardToken: string): string {
        const statistics = [];
        const pluginPrefix = rewardToken ? rewardToken + '/' : '';

        for (const source in betSource.live) {
            statistics.push(
                `${pluginPrefix}${TransactionAffiliate.Livebook};${typeId}/${TransactionAffiliate.Livebook}/MS2/${source};${betSource.live[source]};${stakePerPick}`,
            );
        }

        for (const source in betSource.main) {
            statistics.push(
                `${pluginPrefix}${TransactionAffiliate.Mainbook};${typeId}/${TransactionAffiliate.Mainbook}/MS2/${source};${betSource.main[source]};${stakePerPick}`,
            );
        }

        return statistics.join(',');
    }

    static getStakePerPick(stake: number, pickCount: number, betType: BetslipType | null): number {
        if (!stake) {
            return 0;
        }

        return betType === BetslipType.Single ? stake : stake / pickCount;
    }

    static getPickTrackingName(pick: BetslipPick): string {
        if (!pick.tracking) {
            return '';
        }

        return (pick.tracking.source || '') + (pick.tracking.sourceOfBetSuffix || '');
    }

    static mapBetslipType(slipType: BetSlipType): BetslipType {
        switch (slipType) {
            case BetSlipType.Combo:
                return BetslipType.Combo;
            case BetSlipType.System:
                return BetslipType.System;
            case BetSlipType.Single:
            default:
                return BetslipType.Single;
        }
    }

    static getRewardToken(
        tokens: TokensRecord,
        typeState: IBetslipTypeState,
        betslipType: BetslipType | null,
        picks: BetslipPick[],
        pickCount: number = 1,
    ): string {
        if (betslipType === BetslipType.Combo || betslipType === BetslipType.Teaser) {
            const context = { betslipType };

            const token = getSelectedTokenForContext(context, tokens, typeState);

            return this.getTokenString(token, pickCount);
        }

        if (betslipType !== BetslipType.Single) {
            return '';
        }

        const contexts = picks
            .map((pick) => {
                const context = { betslipType, pickId: pick.id };

                const token = getSelectedTokenForContext(context, tokens, typeState);

                return this.getTokenString(token, pickCount);
            })
            .filter((x) => !!x);

        return contexts.join(',');
    }

    private static getTokenString(token: IRewardToken | null, pickCount: number): string {
        if (!token) {
            return '';
        }

        const acquisitionTitle = token.rewardTargetType === RewardTargetType.WelcomeOffer ? ' - welcome offer' : '';

        switch (token.rewardTokenType) {
            case RewardTokenType.FreeBet:
                return RewardTokenTracking.FreeBet + acquisitionTitle;
            case RewardTokenType.OddsBoost:
                return RewardTokenTracking.OddsBoost + acquisitionTitle;
            case RewardTokenType.RiskFreeBet:
                return RewardTokenTracking.RiskFree + acquisitionTitle;
            case RewardTokenType.BetAndGet:
                return 'bet & get' + (token.customTitle ? ': ' + token.customTitle : '') + acquisitionTitle;
            case RewardTokenType.AccaBoost:
                const boostRatio = getAccaBoostRatio(token as AccaBoostToken, pickCount);
                const boostPercentage = new Decimal(boostRatio).times(100);

                return `${RewardTokenTracking.AccaBoost} | ${boostPercentage}% | ${pickCount} selections`;
            default:
                return '';
        }
    }

    static getAdditional(tracking: IPickTracking): TrackingData {
        return {
            [trackingConstants.MARQUEE_CONTENT_LOGIC]: tracking.additional?.[trackingConstants.MARQUEE_CONTENT_LOGIC] || NOT_APPLICABLE,
            [trackingConstants.COMPONENT_MODULE_CUSTOM_NAME]: tracking.additional?.[trackingConstants.COMPONENT_MODULE_CUSTOM_NAME] || '',
            [trackingConstants.COMPONENT_MODULE_NAME]: tracking.additional?.[trackingConstants.COMPONENT_MODULE_NAME] || '',
            [trackingConstants.COMPONENT_MODULE_POSITION]: tracking.additional?.[trackingConstants.COMPONENT_MODULE_POSITION] || '',
            [trackingConstants.COMPONENT_MODULE_SOURCE]: tracking.additional?.[trackingConstants.COMPONENT_MODULE_SOURCE] || '',
            [trackingConstants.COMPONENT_PAGE_LAYOUT]: tracking.additional?.[trackingConstants.COMPONENT_PAGE_LAYOUT] || NOT_APPLICABLE,
            [trackingConstants.MARQUEE_NAME]: tracking.additional?.[trackingConstants.MARQUEE_NAME] || NOT_APPLICABLE,
            [trackingConstants.MARQUEE_TYPE]: tracking.additional?.[trackingConstants.MARQUEE_TYPE] || NOT_APPLICABLE,
            [trackingConstants.BET_CALENDER]: tracking.additional?.[trackingConstants.BET_CALENDER] || NOT_APPLICABLE,
            [trackingConstants.SITECORE_TEMPLATE_ID]: tracking.additional?.[trackingConstants.SITECORE_TEMPLATE_ID] || NOT_APPLICABLE,
            [trackingConstants.COMPONENT_MODULE_TYPE]: tracking.moduleType || NOT_APPLICABLE,
        };
    }

    static getSku(pick: BetslipPick): string {
        switch (pick.id.getPickType()) {
            case PickType.V1Pick:
                const v1Pick = pick as BetslipV1Pick;

                return [v1Pick.sportId, v1Pick.league.id, v1Pick.id.eventId, v1Pick.id.optionId].join('-');
            case PickType.V2OptionMarketPick:
                const optionPick = pick as BetslipV2OptionMarketPick;

                return [optionPick.fixture.sportId, optionPick.fixture.league?.id, optionPick.id.fixtureId, optionPick.id.optionId].join('-');
            case PickType.V2ParticipantPick:
                const participantPick = pick as BetslipV2ParticipantPick;

                return [
                    participantPick.sportId,
                    participantPick.id.marketId,
                    participantPick.id.fixtureId,
                    participantPick.id.participants && participantPick.id.participants.join('_'), // TODO || participantPick.freeformedOption!.id,
                ].join('-');
            case PickType.BetBuilderPick:
                const betBuilderPick = pick as BetslipBetBuilderPick;

                return [betBuilderPick.sportId, betBuilderPick.market.id, betBuilderPick.eventId, betBuilderPick.optionId].join('-');
            default:
                const v2Pick = pick as BetslipV2Pick;

                return [v2Pick.sportId, v2Pick.fixture.league?.id, v2Pick.fixture.fixtureId, v2Pick.regionId].join('-');
        }
    }

    static getProductCategory(pick: BetslipPick, betType: BetslipType | null, isEachWaySelected?: boolean, types?: IBetslipTypeState): string {
        switch (this.mapTrackingPickType(pick)) {
            case PickTypeTracking.Sport:
            case PickTypeTracking.BetBuilder:
                const category = betType
                    ? betType === BetslipType.Teaser
                        ? BetslipTrackingHelper.toUpperFirst(betType) + '/ teaser'
                        : BetslipTrackingHelper.toUpperFirst(betType)
                    : PickTypeTracking.Sport;
                if (!!types && this.getEachWayState(pick, types)) {
                    return [category, BetType.EachWay].join('_');
                }

                return category;

            case PickTypeTracking.Race:
                const participantbetType = this.mapParticipantBetType(pick);
                const marketType = isEachWaySelected ? BetType.EachWay : !participantbetType ? pick.market.marketType : participantbetType;
                const priceType = this.mapPriceType(pick.priceType);

                return [PickTypeTracking.Sport, marketType, priceType].join('_');
            default:
                return '-';
        }
    }

    static getLeagueId(pick: BetslipPick): string {
        return BetslipV1Pick.isPick(pick) ? pick?.league.id.toString() : (pick as BetslipV2Pick)?.leagueId.toString();
    }

    static getResultId(pick: BetslipPick): string | null {
        if (BetslipV1Pick.isPick(pick)) {
            return pick?.option.id.toString();
        }

        if (BetslipV2OptionMarketPick.isPick(pick)) {
            return pick?.option.id.toString();
        }

        if (BetslipV2ParticipantPick.isPick(pick)) {
            return Object.values(pick.id?.participantIds).join('-');
        }

        return null;
    }

    static getPickDetails(pick: BetslipPick): string {
        switch (pick.id.getPickType()) {
            case PickType.V2ParticipantPick:
                const participantPick = pick as BetslipV2ParticipantPick;
                let participantName: string;
                if (participantPick.fixture.sportId === SportConstant.Greyhounds) {
                    const raceParticipants = participantPick.participants
                        ?.sort((a, b) => a.position - b.position)
                        .map((participant) => {
                            return participant.name.name;
                        });

                    participantName = participantPick.participants && participantPick.participants.length ? raceParticipants.join(':') : '';
                } else participantName = participantPick.participants.length > 0 ? participantPick.participants[0].name.name : '';

                return [participantPick.eventName.name, ParticipantPickType[participantPick.id.pickType].toString(), participantName].join(';');
            case PickType.BetBuilderPick:
            case PickType.V1Pick:
            case PickType.V2OptionMarketPick:
            default:
                if (pick.tracking.sourceOfPick === SourceOfPick.TeamGrouping) {
                    return [pick.eventName.name, pick.tracking.betType, pick.tracking.controllerTabName, pick.tracking.playerName].join(';');
                }

                return [pick.eventName.name, pick.marketName.name, pick.optionName.name].join(';');
        }
    }

    static mapPriceType(priceType: PriceType): PriceTypeTracking {
        switch (priceType) {
            case PriceType.Fixed:
                return PriceTypeTracking.Fixed;
            case PriceType.StartingPrice:
                return PriceTypeTracking.StartingPrice;
            case PriceType.NoPrice:
            default:
                return PriceTypeTracking.NoPrice;
        }
    }

    static mapTrackingPickType(pick: BetslipPick): PickTypeTracking | null {
        switch (pick.id.getPickType()) {
            case PickType.V1Pick:
            case PickType.V2OptionMarketPick:
                return BetslipV2HorseRaceOptionMarketPick.isPick(pick) ? PickTypeTracking.Race : PickTypeTracking.Sport;
            case PickType.V2ParticipantPick:
                return BetslipV2HorseRaceWinParticipantPick.isPick(pick) ||
                    BetslipV2HorseRaceXCastPick.isPick(pick) ||
                    BetslipV2HorseRaceOptionMarketPick.isPick(pick)
                    ? PickTypeTracking.Race
                    : PickTypeTracking.Sport;
            case PickType.BetBuilderPick:
                return PickTypeTracking.BetBuilder;
            default:
                return null;
        }
    }

    static mapParticipantBetType(pick: BetslipPick): BetType | undefined {
        if (BetslipV2ParticipantPick.isPick(pick) || BetslipV2HorseRaceOptionMarketPick.isPick(pick)) {
            const pickType = pick instanceof BetslipV2HorseRaceOptionMarketPick ? pick.pickType : pick.id.pickType;
            switch (pickType) {
                case ParticipantPickType.Win:
                    return (pick.market as IBetslipV2PickWinParticipantMarket).isEachWay ? BetType.EachWay : BetType.Win;
                case ParticipantPickType.Forecast:
                    return BetType.Forecast;
                case ParticipantPickType.CombinationForecast:
                    return BetType.CombinationForecast;
                case ParticipantPickType.Tricast:
                    return BetType.Tricast;
                case ParticipantPickType.CombinationTricast:
                    return BetType.CombinationTricast;
            }
        }

        return;
    }

    static getBetDetails(tracking: IPickTracking, isterminal: boolean): string {
        if (!isterminal) {
            if (tracking.additional?.[trackingConstants.MARQUEE_TYPE] === MarqueeType.Special) {
                return 'price boost' + '-' + (tracking.source || '') + (tracking.sourceOfBetSuffix || '');
            }

            return 'price boost - Competitions page';
        } else {
            return 'price boost - BetStation Competitions page';
        }
    }

    static getEachWayState(pick: BetslipPick, types: IBetslipTypeState): boolean {
        switch (types.base.currentSelectedType) {
            case BetslipType.Single:
                return !!types.singleBet.picks[pick.id.toString()]?.isEachWay;
            case BetslipType.Combo:
                return types.comboBet.isEachWay;
            case BetslipType.System:
                return types.systemBet.isEachWay;
            default:
                return false;
        }
    }

    static toUpperFirst = (value: string): string => upperFirst(toLower(value));

    static getLegBetDetails(pick: BetslipPick, groupId: string): string {
        const provider = this.getBetBuilderProvider(pick.groupInfo);
        const sgpTracking = provider + ';' + groupId;

        if (pick.tracking.betDetails) {
            if (pick.tracking.sourceOfPick === SourceOfPick.EmbeddedBetBuilder || pick.tracking.sourceOfPick === SourceOfPick.OverlayBetBuilder) {
                return pick.tracking.betDetails;
            }

            return pick.tracking.betDetails + ';' + sgpTracking;
        }

        return sgpTracking;
    }

    static getBetBuilderProvider(groupInfo?: BetslipGroupInformation): BetBuilderTrackingValues | null {
        switch (groupInfo?.provider) {
            case BetBuilderProvider.Angstrom:
                return BetBuilderTrackingValues.AngstromSgp;
            case BetBuilderProvider.BybFootball:
                return BetBuilderTrackingValues.InHouseFootball;
            default:
                return null;
        }
    }

    static getSuccessSlipTrackingBetslipType(slip: ISucceededSlipResult): BetslipType {
        const slipType = mapBetslipType(slip.betType);

        if (slipType === BetslipType.System || slipType === BetslipType.Single) {
            return slipType;
        }

        if (slip.isMultiTeaser) {
            return BetslipType.Teaser;
        }

        return BetslipType.Combo;
    }
}
